
type StreamDeckState = {
  page: string;
  selectedTeam?: string;
  selectedTimePeriod?: string;
};

type KeyHandler = (key: number) => void;

const WS_URL = "ws://127.0.0.1:5051";
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY_MS = 1500;

let ws: WebSocket | null = null;
let reconnectTimer: number | null = null;
let reconnectAttempts = 0;
let streamDeckUnavailable = false;

let pendingState: StreamDeckState | null = null;
let lastSentStateRaw = "";


let heartbeatSubscribers: (() => void)[] = [];

export function subscribeHeartbeats(callback: () => void) {
  heartbeatSubscribers.push(callback);

  return () => {
    heartbeatSubscribers = heartbeatSubscribers.filter((cb) => cb !== callback);
  };
}



const keyHandlers = new Set<KeyHandler>();

function isSocketOpen() {
  return ws?.readyState === WebSocket.OPEN;
}

function isSocketConnecting() {
  return ws?.readyState === WebSocket.CONNECTING;
}

function clearReconnectTimer() {
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect() {
  if (streamDeckUnavailable) return;

  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    streamDeckUnavailable = true;
    console.info(
      `[StreamDeck] Helper not available after ${MAX_RECONNECT_ATTEMPTS} attempts. Giving up.`
    );
    return;
  }

  reconnectAttempts += 1;

  console.info(
    `[StreamDeck] Retry ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${RECONNECT_DELAY_MS}ms`
  );

  clearReconnectTimer();

  reconnectTimer = window.setTimeout(() => {
    connectStreamDeckHelper();
  }, RECONNECT_DELAY_MS);
}

export function connectStreamDeckHelper() {
  if (streamDeckUnavailable) return;

  if (isSocketOpen() || isSocketConnecting()) return;

  try {
    console.info("[StreamDeck] Connecting to helper...");

    ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      console.info("[StreamDeck] Connected to helper");

      reconnectAttempts = 0;
      streamDeckUnavailable = false;
      clearReconnectTimer();

      ws?.send(
        JSON.stringify({
          type: "identify",
          client: "app",
        })
      );

      if (pendingState) {
        sendState(pendingState);
      }
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      
    if (msg.type === "heartbeat") {
      heartbeatSubscribers.forEach((cb) => cb());
      return;
    }
    
      if (msg.type === "key-pressed") {
        keyHandlers.forEach((handler) => handler(msg.key));
      }
    };

    ws.onerror = () => {
      console.info("[StreamDeck] Helper connection failed");
    };

    ws.onclose = () => {
      ws = null;
      scheduleReconnect();
    };
  } catch (err) {
    console.info("[StreamDeck] Could not create WebSocket", err);
    ws = null;
    scheduleReconnect();
  }
}

function sendState(state: StreamDeckState) {
  pendingState = state;

  const payload = {
    type: "state-update",
    ...state,
  };

  const raw = JSON.stringify(payload);

  if (raw === lastSentStateRaw) return;

  if (!isSocketOpen()) {
    connectStreamDeckHelper();
    return;
  }

  lastSentStateRaw = raw;
  ws?.send(raw);
}

export function updateStreamDeckState(state: StreamDeckState) {
  sendState(state);
}

export function subscribeStreamDeckKeys(handler: KeyHandler) {
  keyHandlers.add(handler);
  connectStreamDeckHelper();

  return () => {
    keyHandlers.delete(handler);
  };
}

// Optional manual retry, useful if helper starts after the app
export function retryStreamDeckConnection() {
  streamDeckUnavailable = false;
  reconnectAttempts = 0;
  connectStreamDeckHelper();
}


